import { useState } from 'react';
import clsx from 'clsx';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, InlineStat, StatTile } from '../../components/ui/Card';
import { InfoTip } from '../../components/ui/InfoTip';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { Table, Td, Tr } from '../../components/ui/Table';
import { Link } from 'react-router-dom';
import { useSubscriptionsSummary } from '../../hooks/useFinance';
import { useHotAccounts, useRunPipeline, useSignalCoverage, useSignalOpsHealth } from '../../hooks/useSignalOps';
import type { SignalCoverageRow } from '../../api/signalOps';
import { formatDayMonthYear } from '../../utils/dateFormat';
import { formatNumber } from '../../utils/numberFormat';
import { daysAgo, humanize } from './format';
import { channelLabel, signalLabel } from './labels';
import { DEMO_BOOKED_DEFINITIONS, demoBookedNote, ledgerBridge } from './healthBridge';

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return <InlineStat label={label} value={value} sub={hint} size="sm" />;
}

const n = (v: number) => formatNumber(v);

/**
 * "3.4h ago", or "just now" when the newest timestamp lies in the future. A future
 * timestamp is a data problem (an emitter writing local time as UTC, or a dated
 * event such as a scheduled meeting), so it is flagged rather than shown as "−2.7h ago".
 */
function newestSignal(hours: number | null, newestAt: string | null): { value: string; sub?: string; tooltip?: string } {
  if (hours === null) return { value: 'None' };
  const exact = newestAt ? `Newest occurred_at: ${new Date(newestAt).toISOString()}` : undefined;
  if (hours < 0) {
    return {
      value: 'just now',
      sub: `Timestamp is ${Math.abs(hours).toFixed(1)}h in the future, likely a timezone issue at the source`,
      tooltip: exact,
    };
  }
  // The API clamps a future timestamp to 0 hours; read the real age from newestAt when we have it.
  const ageMs = newestAt ? Date.now() - new Date(newestAt).getTime() : hours * 3_600_000;
  if (ageMs < 60_000) {
    return {
      value: 'just now',
      ...(ageMs < 0
        ? { sub: 'Timestamp is in the future, likely a timezone issue at the source' }
        : {}),
      ...(exact ? { tooltip: exact } : {}),
    };
  }
  const value =
    ageMs < 3_600_000
      ? `${Math.floor(ageMs / 60_000)}m ago`
      : hours < 48
        ? `${Math.max(1, Math.round(hours))}h ago`
        : `${Math.round(hours / 24)}d ago`;
  return { value, ...(exact ? { tooltip: exact } : {}) };
}

/** A quiet two-column list: label, then a right-aligned count. */
function CountList({ rows }: { rows: Array<{ key: string; label: string; value: string; sub?: string; warn?: boolean }> }) {
  return (
    <Table>
      <tbody>
        {rows.map((r) => (
          <Tr key={r.key}>
            <Td className="text-text-secondary">
              {r.label}
              {r.sub && <p className={clsx('text-body-xs', r.warn ? 'text-warning-dark' : 'text-text-tertiary')}>{r.sub}</p>}
            </Td>
            <Td align="right" className="tabular-nums text-text-primary">
              {r.value}
            </Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Fallback for an older API that does not send `staleDays`. */
const STALE_EMITTER_DAYS = 7;

interface SilentLine {
  key: string;
  text: string;
  /** Never wrote is a broken emitter (red); stale is one that stopped (amber). */
  tone: 'never_wrote' | 'stale';
}

/** The API's one silent-emitter verdict, with a client fallback for an older build. */
function silenceOf(r: SignalCoverageRow, staleDays: number): SignalCoverageRow['silence'] {
  if (r.silence !== undefined) return r.silence;
  if (r.state === 'no_data') return 'never_wrote';
  if (r.state !== 'live' || !r.newestAt) return null;
  const iso = r.newestAt.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00');
  return new Date(iso).getTime() < Date.now() - staleDays * 86_400_000 ? 'stale' : null;
}

/** Silent emitters grouped by channel: the same set Signal model counts. */
function silentByChannel(rows: SignalCoverageRow[], staleDays: number): Array<{ channel: string; lines: SilentLine[] }> {
  const groups = new Map<string, SilentLine[]>();
  for (const r of rows) {
    const silence = silenceOf(r, staleDays);
    if (!silence) continue;
    const name = signalLabel(r.signalType, r.label);
    const text =
      silence === 'never_wrote'
        ? `${name}: the emitter exists and has written nothing`
        : `${name}: last row ${daysAgo(r.newestAt!.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'))}`;
    const list = groups.get(r.channel) ?? [];
    list.push({ key: r.signalType, text, tone: silence });
    groups.set(r.channel, list);
  }
  return [...groups.entries()]
    .map(([channel, lines]) => ({ channel, lines: lines.sort((a, b) => a.tone.localeCompare(b.tone)) }))
    // Channels with a never-wrote emitter first.
    .sort((a, b) => Number(b.lines.some((l) => l.tone === 'never_wrote')) - Number(a.lines.some((l) => l.tone === 'never_wrote')));
}

/**
 * Silent emitters: amber, not red (an emitter that never wrote is a gap to
 * close, not an outage), grouped by channel and folded by default so eight
 * lines don't shout over the rest of the page.
 */
function SilentEmitters({
  groups,
  silentCount,
  neverWrote,
  quietCount,
  staleDays,
}: {
  groups: Array<{ channel: string; lines: SilentLine[] }>;
  silentCount: number;
  neverWrote: number;
  quietCount: number;
  staleDays: number;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Card padding="sm" className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="flex items-center gap-1.5 text-body-md font-semibold text-warning-dark">
            <AlertTriangle className="h-4 w-4 flex-none" aria-hidden />
            {silentCount} silent {silentCount === 1 ? 'emitter' : 'emitters'} in {groups.length} channel
            {groups.length === 1 ? '' : 's'}
          </h3>
          <p className="mt-0.5 text-body-xs text-text-tertiary">
            {silentCount} = {neverWrote} never wrote + {quietCount} nothing new in {staleDays} days · live from the signal
            ledger, as GTM › Signal model shows
          </p>
        </div>
        <div className="flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-body-xs text-text-secondary hover:text-text-primary"
              aria-expanded={open}
            >
              <ChevronRight className={clsx('h-3.5 w-3.5 transition-transform', open && 'rotate-90')} aria-hidden />
              {open ? 'Hide' : 'Show'}
            </button>
            <Link to="/integrations" className="text-body-xs text-accent hover:underline">
              Open Integrations
            </Link>
          </div>
        </div>
      </div>
      {open && (
        <div className="grid gap-3 sm:grid-cols-2">
          {groups.map((g) => (
            <div key={g.channel} className="space-y-0.5">
              <p className="eyebrow text-text-tertiary">{channelLabel(g.channel)}</p>
              {g.lines.map((e) => (
                <p key={e.key} className="text-body-sm text-text-secondary">
                  <span
                    className={clsx(
                      'mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle',
                      e.tone === 'never_wrote' ? 'bg-warning' : 'bg-warning/40',
                    )}
                    aria-hidden
                  />
                  <Link to="/integrations" className="hover:underline">
                    {e.text}
                  </Link>
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * Data health, and the only place the engine is run from.
 *
 * Nothing about Signal Ops is scheduled yet. That is deliberate: the signal
 * weights are explicit first guesses and the identity matcher deserves a few
 * reviewed passes before anyone acts on its output. A cron would quietly bake
 * both in.
 */
export function HealthView() {
  const { data, isLoading, isError, error, refetch } = useSignalOpsHealth();
  const run = useRunPipeline();
  // The run itself is not recorded; the latest scoring pass is its last stage that leaves a date.
  const lastScored = useHotAccounts({ limit: 1 });
  const coverage = useSignalCoverage();
  // Finance's paying customers, to check the customer suppression against (absent without Finance access).
  const subscriptions = useSubscriptionsSummary();

  if (isLoading) return <LoadingState rows={5} />;
  if (isError) return <ErrorState title="Couldn’t load data health" error={error} onRetry={() => refetch()} />;

  if (!data) return null;

  const result = run.data;
  const newest = newestSignal(data.ledgerAgeHours, data.ledger.newestAt);
  const staleDays = coverage.data?.staleDays ?? STALE_EMITTER_DAYS;
  const silentGroups = coverage.data ? silentByChannel(coverage.data.rows, staleDays) : [];
  // The counts come from the API's own verdict (the numbers Signal model shows);
  // the lines are the same rows, so a mismatch would only mean an older build.
  const silentCount = coverage.data?.silent ?? silentGroups.reduce((n, g) => n + g.lines.length, 0);
  const neverWrote =
    coverage.data?.noData ?? silentGroups.reduce((n, g) => n + g.lines.filter((l) => l.tone === 'never_wrote').length, 0);
  const quietCount = coverage.data?.stale ?? silentCount - neverWrote;
  const customerActions = data.ledger.customerActions30d;
  const bridge = ledgerBridge(data.ledger);
  const demoNote = demoBookedNote(data.ledger.byChannel);

  return (
    <div className="space-y-5">
      <Card padding="sm" className="space-y-4">
        <CardHeader
          title="Pipeline"
          description={[
            'Registers → identity → do-not-contact → signals → scoring → attribution → plays',
          ]
            .filter(Boolean)
            .join(' · ')}
          action={
            <div className="flex items-center gap-2">
              <span className="text-body-xs text-text-tertiary" aria-live="polite">
                {run.isPending
                  ? 'Running now'
                  : result
                    ? result.failedStages.length > 0
                      ? `Just ran: ${result.failedStages.length} failed stage${result.failedStages.length === 1 ? '' : 's'}`
                      : 'Just ran: complete'
                    : lastScored.data?.scoreDate
                      ? `Last run: scoring pass ${formatDayMonthYear(lastScored.data.scoreDate)}`
                      : lastScored.isError
                        ? 'Last run unavailable'
                        : lastScored.isLoading
                          ? null
                          : 'No run recorded yet'}
              </span>
              <InfoTip>Idempotent, so running it twice changes nothing the second time. Nothing here is on a schedule.</InfoTip>
              <Button size="sm" variant="outline" onClick={() => run.mutate()} disabled={run.isPending}>
                {run.isPending ? 'Running…' : 'Run now'}
              </Button>
            </div>
          }
        />

        {run.isError && <ErrorState title="Couldn’t run the pipeline" error={run.error} compact />}

        {!result &&
          (lastScored.isError ? (
            <ErrorState title="Couldn’t read the last scoring pass" error={lastScored.error} compact />
          ) : lastScored.data?.scoreDate || lastScored.isLoading ? null : (
            <EmptyState title="No run recorded yet" description="Run the pipeline to score accounts." compact />
          ))}

        {result && (
          <div className="space-y-3">
            {result.failedStages.length > 0 ? (
              <ErrorState
                title="Finished with failed stages: the numbers below are partial"
                error={result.failedStages.map(humanize).join(', ')}
              />
            ) : (
              <Alert variant="success">Pipeline complete.</Alert>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Canonical companies"
                value={`+${n(result.identity.created)}`}
                hint={`${result.identity.linkedDeterministic} linked on a key, ${result.identity.linkedScored} scored`}
              />
              <Stat
                label="Queued for review"
                value={n(result.identity.queuedForReview)}
                hint="plausible but not certain"
              />
              <Stat
                label="Signals emitted"
                value={n(result.signals.totals.emitted)}
                hint={`${n(result.signals.totals.unresolved)} rows had no resolvable company`}
              />
              <Stat
                label="Accounts scored"
                value={n(result.scoring.scored)}
                hint={`${result.scoring.hot} hot · ${result.scoring.warm} warm · ${result.scoring.cool} cool${
                  result.scoring.freshBookingFloored ? ` · ${result.scoring.freshBookingFloored} raised to warm by a fresh demo booking` : ''
                }`}
              />
              <Stat
                label="Points set by hand"
                value={String(result.scoring.tunedWeightsUsed)}
                hint={`signal types on the Points tab; the rest use the register default${
                  result.scoring.halfLifeDays ? ` · points halve every ${result.scoring.halfLifeDays} days` : ''
                }`}
              />
              <Stat
                label="Firmographics refreshed"
                value={String(result.firmographics.updated)}
                hint={
                  result.firmographics.domainConflicts > 0
                    ? `${result.firmographics.domainConflicts} domain conflicts need a look`
                    : 'no domain conflicts'
                }
              />
              <Stat
                label="Attribution model"
                value={humanize(result.attribution.model)}
                hint={result.attribution.modelReason}
              />
            </div>
            <div className="space-y-1 text-body-xs text-text-tertiary">
              {Object.entries(result.signals.perEmitter).map(([name, r]) => (
                <p key={name}>
                  <span className="text-text-secondary">{humanize(name)}</span>: scanned {n(r.scanned)}, emitted{' '}
                  {n(r.emitted)}, unresolved {n(r.unresolved)}
                  {r.error && <span className="text-error"> · failed: {r.error}</span>}
                </p>
              ))}
              <p>
                Attribution: {result.attribution.bookings} bookings, {result.attribution.attributedRows} rows,{' '}
                {result.attribution.bookingsWithNoTouches} with no prior touches,{' '}
                {result.attribution.unresolvedBookings} unresolved
                {result.attribution.repeatBookingsMerged !== undefined &&
                  `, ${result.attribution.repeatBookingsMerged} repeat bookings merged`}
                {result.attribution.bookingsWithDealValue !== undefined &&
                  `, ${result.attribution.bookingsWithDealValue} valued from an Attio deal`}
                {result.attribution.replaced &&
                  `; table rebuilt (${n(result.attribution.replaced.deleted)} old rows replaced by ${n(result.attribution.replaced.inserted)})`}
                .
              </p>
              {result.plays !== undefined && (
                <p>
                  Plays:{' '}
                  {result.plays === null ? (
                    <span className="text-error">re-evaluation failed, the Plays queue was not refreshed</span>
                  ) : (
                    <>
                      re-evaluated, {n(result.plays.proposed)} proposed
                      {result.plays.warnings.length > 0 && (
                        <span className="text-warning-dark"> · {result.plays.warnings.join(' · ')}</span>
                      )}
                    </>
                  )}
                </p>
              )}
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label="Signals on the ledger"
          value={n(data.ledger.total)}
          sub={[
            `${n(data.ledger.last30d)} in 30 days`,
            `${n(data.ledger.prospectActions30d)} prospect actions`,
            customerActions !== undefined ? `${n(customerActions)} customer logins and usage` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        <StatTile
          label="Newest signal"
          value={newest.value}
          sub={
            newest.sub ??
            (data.stale ? 'Ledger stale: nothing landed in over 36h' : 'Ledger fresh: a signal landed in the last 36h')
          }
          {...(newest.tooltip ? { tooltip: newest.tooltip } : {})}
        />
        <StatTile
          label="Canonical companies"
          value={n(data.coverage.canonicalCompanies)}
          sub={`${n(data.coverage.withSignals)} with any signal, any time`}
          tooltip="Every account with at least one signal on the ledger. Signal model’s base rate counts fewer: only accounts with a signal before their first demo booking."
        />
        <StatTile
          label="Pending match reviews"
          value={n(data.pendingMatchReviews)}
          sub={
            data.pendingMatchReviews > 0
              ? 'Pairs waiting for a call · same count as Match review, pairs with nothing to decide left out'
              : 'Nothing waiting to confirm'
          }
        />
      </div>

      {bridge && (
        <p className={clsx('-mt-2 text-body-xs', bridge.reconciles ? 'text-text-tertiary' : 'text-warning-dark')}>
          {bridge.text}.
        </p>
      )}

      {coverage.isError ? (
        <ErrorState title="Couldn’t check the emitters" error={coverage.error} compact />
      ) : silentCount > 0 ? (
        <SilentEmitters
          groups={silentGroups}
          silentCount={silentCount}
          neverWrote={neverWrote}
          quietCount={quietCount}
          staleDays={staleDays}
        />
      ) : null}

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card padding="sm">
          <CardHeader
            title="Prospect-action signals by channel"
            description={`Last 30 days · by each signal’s channel in the register, the same grouping as Signal model · customer logins and usage${
              customerActions !== undefined ? ` (${n(customerActions)})` : ''
            } counted apart`}
          />
          {data.ledger.byChannel.length === 0 ? (
            <EmptyState title="Nothing on the ledger yet" description="Run the pipeline to fill it." compact />
          ) : (
            <CountList
              rows={data.ledger.byChannel.slice(0, 12).map((c) => ({
                key: c.channel,
                label: channelLabel(c.channel),
                value: n(c.count),
                // The signals behind each channel's count.
                sub: c.types?.length
                  ? `${c.types
                      .slice(0, 3)
                      .map((t) => `${signalLabel(t.type)} ${n(t.count)}`)
                      .join(', ')}${c.types.length > 3 ? ` · ${c.types.length - 3} more type${c.types.length - 3 === 1 ? '' : 's'}` : ''}`
                  : undefined,
              }))}
            />
          )}
          {demoNote && (
            <p className="mt-3 flex items-center gap-1 text-body-xs text-text-tertiary">
              <span>{demoNote}</span>
              <InfoTip label="Why they differ">{DEMO_BOOKED_DEFINITIONS}</InfoTip>
            </p>
          )}
        </Card>

        <Card padding="sm">
          <CardHeader title="Coverage and guardrails" />
          <CountList
            rows={[
              { key: 'scored', label: 'Accounts scored', value: n(data.coverage.withScores) },
              {
                key: 'attr',
                label: 'Accounts with attribution',
                value: n(data.coverage.withAttribution),
                sub: 'Accounts with at least one demo booking traced back to the channels that touched it',
              },
              { key: 'dnc', label: 'Do-not-contact entries', value: n(data.dnc.total) },
              ...data.dnc.byReason.map((r) => ({
                key: `dnc-${r.reason}`,
                label: `Suppressed: ${humanize(r.reason).toLowerCase()}`,
                value: n(r.count),
                ...(r.reason === 'customer'
                  ? {
                      sub: `Domains of Attio companies at stage Customer and of every customer Finance counts as paying${
                        subscriptions.data
                          ? ` (${n(subscriptions.data.summary.payingCustomers)} paying in Odoo this month)${
                              subscriptions.data.summary.payingCustomers > r.count
                                ? `: ${n(subscriptions.data.summary.payingCustomers - r.count)} may not be suppressed`
                                : ''
                            }`
                          : ''
                      }`,
                      warn: !!subscriptions.data && subscriptions.data.summary.payingCustomers > r.count,
                    }
                  : {}),
              })),
            ]}
          />
          {data.dnc.byReason.length === 0 && (
            <p className="mt-3 text-body-xs text-warning-dark">
              No suppressions yet. Run the pipeline; outbound must not send before this list exists.
            </p>
          )}
        </Card>
      </div>
    </div>
  );
}
